import { jsPDF } from 'jspdf';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../config/firebase';
import { Session, SESSION_TYPE_LABELS } from '../models/Session';
import { Company } from '../models/Company';
import { fieldsFor, getTemplate } from '../models/noteTemplates';
import { saveFile } from '../utils/fileExport';

const BURGUNDY: [number, number, number] = [128, 0, 32];
const CHARCOAL: [number, number, number] = [45, 45, 45];
const GRAY: [number, number, number] = [107, 107, 107];

const DEFAULT_COMPANY = 'VERA Health Technologies, LLC';

const fmtDate = (d: Date) => d.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });
const fmtTime = (d: Date) => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
const fmtDateTime = (d: Date) => `${fmtDate(d)} ${fmtTime(d)}`;

const durationOf = (s: Session): string => {
  const start = s.actualStart || s.scheduledStart;
  const end = s.actualEnd || s.scheduledEnd;
  const mins = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

/** Build a branded clinical PDF for a finalized session note. */
export function buildSessionPdf(session: Session, company: Company | null): jsPDF {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 50;
  const contentW = pageW - margin * 2;
  const bottomLimit = pageH - 60;
  let y = margin;

  const ensure = (needed: number) => {
    if (y + needed > bottomLimit) {
      doc.addPage();
      y = margin;
    }
  };

  const start = session.actualStart || session.scheduledStart;
  const companyName = company?.name || DEFAULT_COMPANY;

  /* ---- Header: VERA wordmark + company info ---- */
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(24);
  doc.setTextColor(...BURGUNDY);
  doc.text('VERA', margin, y + 6);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...GRAY);
  const compLines = [
    companyName,
    company?.address || '',
    [company?.phone, company?.email].filter(Boolean).join('  ·  '),
  ].filter(Boolean);
  let cy = y - 2;
  compLines.forEach((line) => {
    doc.text(String(line), pageW - margin, cy, { align: 'right' });
    cy += 12;
  });

  y += 22;
  doc.setDrawColor(...BURGUNDY);
  doc.setLineWidth(1.5);
  doc.line(margin, y, pageW - margin, y);
  y += 26;

  /* ---- Document title ---- */
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(...CHARCOAL);
  doc.text(session.sessionType === 'supervision' ? 'Supervision Note' : 'Session Note', margin, y);
  y += 22;

  /* ---- Metadata (two-column key/value grid) ---- */
  const pairs: Array<[string, string]> = [
    ['Client Name', session.clientName || '—'],
    ['Session Date', fmtDate(start)],
    ['Session Time', fmtTime(start)],
    ['Duration', durationOf(session)],
    ['Therapist (RBT)', session.rbtName || '—'],
    ['Supervisor (BCBA)', session.supervisorSignature || session.supervisorName || '—'],
    ['Session Type', SESSION_TYPE_LABELS[session.sessionType]],
    ['CPT Code', ''],
    ['Service Address', session.clientAddress || '—'],
    ['Authorization', ''],
  ];
  const colW = contentW / 2;
  const rowH = 26;
  const boxTop = y;
  const rows = Math.ceil(pairs.length / 2);
  ensure(rows * rowH + 10);
  for (let i = 0; i < pairs.length; i++) {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const x = margin + col * colW;
    const ry = boxTop + row * rowH;
    const [labelText, valueText] = pairs[i];
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...GRAY);
    doc.text(labelText.toUpperCase(), x + 6, ry + 10);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...CHARCOAL);
    doc.text(doc.splitTextToSize(valueText || '—', colW - 12), x + 6, ry + 22);
  }
  // Box around the grid
  doc.setDrawColor(220, 224, 219);
  doc.setLineWidth(0.75);
  doc.rect(margin, boxTop, contentW, rows * rowH);
  y = boxTop + rows * rowH + 26;

  /* ---- Note body ---- */
  const template = getTemplate(session.noteTemplate);
  const fields = fieldsFor(session.noteTemplate);
  const noteData = (session.noteData as Record<string, string> | null) || {};

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...BURGUNDY);
  ensure(28);
  doc.text(`Clinical Note — ${template.label}`, margin, y);
  y += 8;
  doc.setDrawColor(...BURGUNDY);
  doc.setLineWidth(0.5);
  doc.line(margin, y, margin + 120, y);
  y += 18;

  const writeField = (labelText: string, value: string) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...CHARCOAL);
    ensure(16);
    doc.text(labelText, margin, y);
    y += 14;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...CHARCOAL);
    const lines = doc.splitTextToSize(value && value.trim() ? value : '—', contentW);
    lines.forEach((line: string) => {
      ensure(14);
      doc.text(line, margin, y);
      y += 14;
    });
    y += 8;
  };

  fields.forEach((f) => writeField(f.label, noteData[f.key] || ''));

  /* ---- Parent/Caregiver Summary ---- */
  if (session.parentSummary && session.parentSummary.trim()) {
    y += 6;
    ensure(30);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...BURGUNDY);
    doc.text('Parent/Caregiver Summary', margin, y);
    y += 8;
    doc.setDrawColor(...BURGUNDY);
    doc.setLineWidth(0.5);
    doc.line(margin, y, margin + 180, y);
    y += 18;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...CHARCOAL);
    doc.splitTextToSize(session.parentSummary, contentW).forEach((line: string) => {
      ensure(14);
      doc.text(line, margin, y);
      y += 14;
    });
  }

  /* ---- Signature block ---- */
  y += 24;
  ensure(60);
  doc.setDrawColor(...CHARCOAL);
  doc.setLineWidth(0.5);
  doc.line(margin, y, margin + 260, y);
  y += 14;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...CHARCOAL);
  const signer = session.supervisorSignature || session.supervisorName || '—';
  doc.text(`Reviewed and approved by: ${signer}`, margin, y);
  y += 14;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...GRAY);
  doc.text(
    session.approvedAt ? `Approved: ${fmtDateTime(session.approvedAt)}` : 'Approval pending',
    margin,
    y
  );

  /* ---- Watermark + footer on every page ---- */
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    // Light diagonal watermark (drawn with low opacity so text stays readable)
    const anyDoc = doc as any;
    if (typeof anyDoc.saveGraphicsState === 'function' && typeof anyDoc.GState === 'function') {
      anyDoc.saveGraphicsState();
      anyDoc.setGState(new anyDoc.GState({ opacity: 0.07 }));
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(96);
      doc.setTextColor(...BURGUNDY);
      doc.text('VERA', pageW / 2, pageH / 2, { align: 'center', angle: 45 });
      anyDoc.restoreGraphicsState();
    }
    // Footer
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...GRAY);
    doc.text('Generated by VERA Health Technologies, LLC', margin, pageH - 30);
    doc.text(`Page ${i} of ${total}`, pageW - margin, pageH - 30, { align: 'right' });
  }

  return doc;
}

export function downloadBlob(blob: Blob, filename: string): void {
  // saveFile branches web (anchor download) vs native (Filesystem + Share).
  void saveFile(filename, blob, 'application/pdf');
}

/**
 * Generate the branded PDF, trigger a local download, upload it to
 * companies/{companyId}/sessions/{sessionId}/session-note.pdf, and return
 * the Storage download URL.
 */
export async function generateAndStoreSessionPdf(
  session: Session,
  company: Company | null
): Promise<string> {
  const doc = buildSessionPdf(session, company);
  const blob = doc.output('blob');

  const safeClient = (session.clientName || 'client').replace(/[^a-z0-9]+/gi, '_');
  downloadBlob(blob, `VERA_SessionNote_${safeClient}.pdf`);

  const path = `companies/${session.companyId}/sessions/${session.id}/session-note.pdf`;
  const r = storageRef(storage, path);
  await uploadBytes(r, blob, { contentType: 'application/pdf' });
  return getDownloadURL(r);
}
